import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchKids, createKid, renameKid, removeKid, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";
import { X, Plus, Pencil, Trash2, LogOut, HelpCircle } from "lucide-react";

export default function ParentSettingsSheet({ open, onClose, onKidRenamed, onKidDeleted }) {
  const { user, logout, activeKidId, selectKid } = useAuth();
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setKids(await fetchKids());
    } catch (e) {
      toast.error("Couldn't load kid profiles.");
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const kid = await createKid(newName.trim());
      setKids((prev) => [...prev, kid]);
      setNewName("");
      toast.success(`Added ${kid.name}! 🎉`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async (kid) => {
    const next = window.prompt(`Rename ${kid.name} to:`, kid.name);
    if (!next || !next.trim() || next.trim() === kid.name) return;
    try {
      const updated = await renameKid(kid.id, next.trim());
      setKids((prev) => prev.map((k) => (k.id === kid.id ? updated : k)));
      toast.success("Name updated");
      onKidRenamed?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDelete = async (kid) => {
    const ok = window.confirm(
      `Delete ${kid.name}? This erases their balance, goals and history. This can't be undone.`
    );
    if (!ok) return;
    try {
      await removeKid(kid.id);
      setKids((prev) => prev.filter((k) => k.id !== kid.id));
      toast(`Deleted ${kid.name}`);
      if (activeKidId === kid.id) selectKid(null);
      onKidDeleted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div
      data-testid="parent-settings-sheet"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center fade-in"
      style={{ backgroundColor: "rgba(42, 45, 124, 0.45)" }}
      onClick={onClose}
    >
      <div
        className="modal-slide-up w-full sm:max-w-md bg-white rounded-t-[36px] sm:rounded-[36px] border-4 border-navy p-5 sm:p-7 max-h-[92vh] overflow-y-auto"
        style={{ boxShadow: "0 -8px 0 0 #2A2D7C" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-3xl text-navy">PARENT ZONE</h2>
          <button
            data-testid="close-settings-btn"
            onClick={onClose}
            className="w-10 h-10 rounded-full border-3 border-navy flex items-center justify-center bg-white hover:scale-105 transition-transform"
            style={{ boxShadow: "0 3px 0 0 #2A2D7C", borderWidth: 3 }}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-navy" strokeWidth={3} />
          </button>
        </div>
        <p data-testid="settings-account-email" className="font-display text-base text-navy/70 mb-5">
          {user?.email}
        </p>

        <h3 className="font-display text-xl text-navy mb-2">Kid profiles</h3>
        <ul className="space-y-2 mb-4">
          {kids.map((kid) => (
            <li
              key={kid.id}
              data-testid={`settings-kid-${kid.id}`}
              className="flex items-center gap-2 rounded-2xl border-3 border-navy p-3"
              style={{ backgroundColor: "#FFF3C2", borderWidth: 3, boxShadow: "0 3px 0 0 #2A2D7C" }}
            >
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg text-navy truncate">{kid.name}</div>
                <div className="font-display text-sm text-navy/60">
                  {formatMoney(kid.balance || 0)}
                </div>
              </div>
              <button
                data-testid={`rename-kid-${kid.id}`}
                onClick={() => handleRename(kid)}
                className="w-10 h-10 rounded-full border-3 border-navy bg-white flex items-center justify-center hover:scale-105 transition-transform"
                style={{ borderWidth: 3, boxShadow: "0 3px 0 0 #2A2D7C" }}
                aria-label={`Rename ${kid.name}`}
              >
                <Pencil className="w-4 h-4 text-navy" strokeWidth={3} />
              </button>
              <button
                data-testid={`delete-kid-${kid.id}`}
                onClick={() => handleDelete(kid)}
                className="w-10 h-10 rounded-full border-3 border-navy flex items-center justify-center hover:scale-105 transition-transform"
                style={{
                  backgroundColor: "#FF5A36",
                  borderWidth: 3,
                  boxShadow: "0 3px 0 0 #2A2D7C",
                }}
                aria-label={`Delete ${kid.name}`}
              >
                <Trash2 className="w-4 h-4 text-white" strokeWidth={3} />
              </button>
            </li>
          ))}
        </ul>

        <form onSubmit={handleAdd} className="flex gap-2 mb-6">
          <input
            data-testid="settings-new-kid-input"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Add a kid (first name)"
            className="flex-1 min-w-0 font-display text-lg text-navy rounded-2xl border-4 border-navy px-4 py-3 outline-none bg-white"
            style={{ boxShadow: "0 4px 0 0 #2A2D7C" }}
            maxLength={30}
          />
          <button
            data-testid="settings-add-kid-btn"
            type="submit"
            disabled={busy || !newName.trim()}
            className="chunky-btn px-4 text-lg text-white flex items-center gap-1 disabled:opacity-50"
            style={{ backgroundColor: "#FF4D9D" }}
          >
            <Plus className="w-5 h-5" strokeWidth={4} />
            ADD
          </button>
        </form>

        <button
          data-testid="settings-how-it-works-btn"
          onClick={() => navigate("/how-it-works")}
          className="chunky-btn w-full py-3 text-xl text-navy flex items-center justify-center gap-2 mb-3 bg-white"
        >
          <HelpCircle className="w-5 h-5" strokeWidth={3} />
          HOW IT WORKS
        </button>

        <button
          data-testid="logout-btn"
          onClick={logout}
          className="chunky-btn w-full py-3 text-xl text-navy flex items-center justify-center gap-2"
          style={{ backgroundColor: "#FFC400" }}
        >
          <LogOut className="w-5 h-5" strokeWidth={3} />
          LOG OUT
        </button>
      </div>
    </div>
  );
}
